import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';

const ToastContext = createContext(null);

const ICONS = { success: CheckCircle2, error: XCircle, warning: AlertTriangle, info: Info };
const ACCENTS = {
  success: 'border-l-emerald-400 text-emerald-300',
  error: 'border-l-red-400 text-red-300',
  warning: 'border-l-amber-400 text-amber-300',
  info: 'border-l-sky-400 text-sky-300'
};

let idCounter = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timers = useRef({});

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    if (timers.current[id]) { clearTimeout(timers.current[id]); delete timers.current[id]; }
  }, []);

  const showToast = useCallback(({ message, type = 'info', action, duration }) => {
    const id = ++idCounter;
    const ms = duration ?? (action ? 6000 : 4000);
    setToasts(prev => [...prev, { id, message, type, action }]);
    timers.current[id] = setTimeout(() => removeToast(id), ms);
    return id;
  }, [removeToast]);

  const toast = useCallback((message, options = {}) => showToast({ message, ...options }), [showToast]);
  toast.success = (message, options) => showToast({ message, type: 'success', ...options });
  toast.error = (message, options) => showToast({ message, type: 'error', ...options });
  toast.warning = (message, options) => showToast({ message, type: 'warning', ...options });
  toast.info = (message, options) => showToast({ message, type: 'info', ...options });

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="fixed z-[100] inset-x-3 bottom-[calc(86px+env(safe-area-inset-bottom))] sm:inset-x-auto sm:right-5 sm:bottom-5 flex flex-col gap-2 sm:w-80 pointer-events-none">
        {toasts.map(item => {
          const Icon = ICONS[item.type] || Info;
          return (
            <div
              key={item.id}
              role={item.type === 'error' ? 'alert' : 'status'}
              aria-live={item.type === 'error' ? 'assertive' : 'polite'}
              className={`retro-frame pointer-events-auto bg-[#12151d] border-l-4 ${ACCENTS[item.type] || ACCENTS.info} p-3.5 flex items-start gap-2.5 animate-slideUp`}
            >
              <Icon className="w-4 h-4 shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-white leading-snug">{item.message}</p>
                {item.action && (
                  <button
                    onClick={() => { item.action.onClick(); removeToast(item.id); }}
                    className="mt-2 text-[10px] font-extrabold uppercase tracking-wider text-lime-400 hover:text-lime-300"
                  >
                    {item.action.label}
                  </button>
                )}
              </div>
              <button onClick={() => removeToast(item.id)} aria-label="Fechar notificação" className="shrink-0 text-slate-500 hover:text-white p-0.5">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast deve ser usado dentro de ToastProvider');
  return ctx.toast;
};
